/**
 * Components a chat reply can carry beside its text — a quote you can adjust,
 * a table of who owes you, a week laid out as a timeline.
 *
 * 🚨 The SERVER builds every one of these, from a tool's own result. The model
 * never writes a block and never writes a number on one; it only chooses which
 * tool to call. That is the property the whole chat already rests on (every
 * figure on a card is built server-side, and the amount-claim guard exists
 * because the model gets money wrong), and it is why there is no generic
 * "compose a view" tool. See docs/intelligent-chat-answers-plan.md.
 *
 * 🚨 The block is EXTRA. The reply's sentence always carries the answer on its
 * own ("Draft Q-1042 is £2,292.60 inc VAT"), because a block can fail to
 * render, an older client cannot draw a kind it does not know, and the grader
 * reads text first.
 *
 * Pure and directive-free: the route, the panel, the Mucka Bar and the
 * oversight grader all import it (anti-pattern #18).
 */

import { summariseApprovalStackBlock, type ApprovalStackBlock } from './blocks/approval-stack';
import { summariseDocumentCardBlock, type DocumentCardBlock } from './blocks/document-card';
import { summariseRevenueChartBlock, type RevenueChartBlock } from './blocks/revenue-chart';
import { summariseClientChoicesBlock, type ClientChoicesBlock } from './blocks/client-choices';
import { summariseJobBriefBlock, type JobBriefBlock } from './blocks/job-brief';
import { summariseQuoteHistoryBlock, type QuoteHistoryBlock } from './blocks/quote-history';
import { summarisePriceRangeBlock, type PriceRangeBlock } from './blocks/price-range';
import { summariseCertsDueBlock, type CertsDueBlock } from './blocks/certs-due';
import { summarisePhotoPickerBlock, type PhotoPickerBlock } from './blocks/photo-picker';
import { summariseAvailabilityStripBlock, type AvailabilityStripBlock } from './blocks/availability-strip';
import { summariseSlotPickerBlock, type SlotPickerBlock } from './blocks/slot-picker';
import { summariseTeamMapBlock, type TeamMapBlock } from './blocks/team-map';
import { summariseCallCardsBlock, type CallCardsBlock } from './blocks/call-cards';
import { summarisePartsChecklistBlock, type PartsChecklistBlock } from './blocks/parts-checklist';
import { summariseReceiptLinesBlock, type ReceiptLinesBlock } from './blocks/receipt-lines';
import { summariseMorningBriefingBlock, type MorningBriefingBlock } from './blocks/morning-briefing';

export type {
  ApprovalStackBlock, DocumentCardBlock, RevenueChartBlock, ClientChoicesBlock,
  JobBriefBlock, QuoteHistoryBlock, PriceRangeBlock, CertsDueBlock, PhotoPickerBlock,
  AvailabilityStripBlock, SlotPickerBlock, TeamMapBlock, CallCardsBlock, PartsChecklistBlock,
  ReceiptLinesBlock, MorningBriefingBlock,
};

/** One quote line, as the editor shows it. Money is in pounds, as stored. */
export interface LineEditorLine {
  /** `quote_line_items.id` — what a step names. */
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  /** quantity × unitPrice as the server stored it, never recomputed here. */
  lineTotal: number;
  /** An option or an optional extra: shown, but not steppable from the chat. */
  steppable: boolean;
}

export interface LineEditorBlock {
  kind: 'line-editor';
  quoteId: string;
  reference: string;
  /** Only a draft is steppable from the chat; anything else is shown read-only. */
  status: string;
  lines: LineEditorLine[];
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  total: number;
}

/** One overdue invoice, with what a Chase tap would send. */
export interface OwedRow {
  invoiceId: string;
  reference: string;
  /** Who it is to. Built server-side from the client record. */
  who: string;
  /** Outstanding, in pounds. */
  amount: number;
  /** Null when the invoice is marked overdue but has no due date to count from. */
  daysOverdue: number | null;
  /** Where the row opens. */
  path: string;
  /** False when there is nobody to send a reminder to (no email on file). */
  chaseable: boolean;
}

export interface OwedTableBlock {
  kind: 'owed-table';
  rows: OwedRow[];
  totalOwed: number;
  /** Rows beyond what the table shows. Never hidden silently. */
  more: number;
}

export interface TimelineStop {
  jobId: string;
  /** "08:00", London time, built server-side. */
  time: string | null;
  title: string;
  /** Postcode or short address. */
  where: string | null;
  /** Who is on it: "You", an engineer's first name, or null when unassigned. */
  who: string | null;
  pencilled: boolean;
  path: string;
}

export interface TimelineDay {
  /** YYYY-MM-DD, London. */
  date: string;
  /** "Mon 12", built server-side so every surface says the same thing. */
  label: string;
  stops: TimelineStop[];
}

export interface WeekTimelineBlock {
  kind: 'week-timeline';
  days: TimelineDay[];
  /** The day the reply is about, preselected. */
  focusDate: string;
}

/**
 * A record that the reply's job rows were on screen (`show_job_list`). The rows
 * themselves still travel as `jobList` and draw as they always have; this only
 * exists so the stored reply, and therefore the grader, knows a list was shown
 * — without it a reply saying "six jobs, they're on screen" reads to the grader
 * as a list it was never given (list-read-as-prose, rubric v3). No renderer.
 */
export interface JobListShownBlock {
  kind: 'job-list';
  count: number;
}

export type UiBlock =
  | LineEditorBlock | OwedTableBlock | WeekTimelineBlock | JobListShownBlock
  | ApprovalStackBlock | DocumentCardBlock | RevenueChartBlock | ClientChoicesBlock
  | JobBriefBlock | QuoteHistoryBlock | PriceRangeBlock | CertsDueBlock | PhotoPickerBlock
  | AvailabilityStripBlock | SlotPickerBlock | TeamMapBlock | CallCardsBlock | PartsChecklistBlock
  | ReceiptLinesBlock | MorningBriefingBlock;
export type UiBlockKind = UiBlock['kind'];

/** Every kind, for the guards that must fail when a new one is not handled. */
export const UI_BLOCK_KINDS: readonly UiBlockKind[] = [
  'line-editor', 'owed-table', 'week-timeline', 'job-list',
  'approval-stack', 'document-card', 'revenue-chart', 'client-choices',
  'job-brief', 'quote-history', 'price-range', 'certs-due', 'photo-picker',
  'availability-strip', 'slot-picker', 'team-map', 'call-cards', 'parts-checklist',
  'receipt-lines', 'morning-briefing',
];

/** What makes two blocks "the same thing", so a later one in a turn replaces it. */
export function uiBlockKey(block: UiBlock): string {
  switch (block.kind) {
    case 'line-editor': return `line-editor:${block.quoteId}`;
    case 'owed-table': return 'owed-table';
    case 'week-timeline': return 'week-timeline';
    case 'job-list': return 'job-list';
    case 'approval-stack': return 'approval-stack';
    // Keyed by the staged card: re-staging the same email in a turn updates
    // the same row, so the block replaces itself.
    case 'document-card': return `document-card:${block.actionId}`;
    case 'revenue-chart': return 'revenue-chart';
    // Keyed by who: two ambiguous names in one turn keep both blocks.
    case 'client-choices': return `client-choices:${block.rows.map(r => r.clientId).join(',')}`;
    case 'job-brief': return `job-brief:${block.jobId}`;
    case 'quote-history': return `quote-history:${block.quoteId}`;
    // One price strip per reply: only the last lookup in a turn shows.
    case 'price-range': return 'price-range';
    case 'certs-due': return 'certs-due';
    case 'photo-picker': return `photo-picker:${block.jobId}`;
    case 'availability-strip': return `availability-strip:${block.date}`;
    case 'slot-picker': return 'slot-picker';
    case 'team-map': return 'team-map';
    case 'call-cards': return 'call-cards';
    case 'parts-checklist': return 'parts-checklist';
    case 'receipt-lines': return `receipt-lines:${block.attachmentId}`;
    case 'morning-briefing': return 'morning-briefing';
  }
}

/**
 * Add a tool's blocks to the turn's. A block with the key of one already held
 * REPLACES it in place: a quote drafted and then edited in one turn shows once,
 * with the edit's totals, not twice.
 */
export function mergeUiBlocks(held: UiBlock[], incoming: readonly UiBlock[]): void {
  for (const block of incoming) {
    const key = uiBlockKey(block);
    const at = held.findIndex(b => uiBlockKey(b) === key);
    if (at === -1) held.push(block);
    else held[at] = block;
  }
  // The price lookups behind a drafted quote are workings, not the answer:
  // lookup_price_list runs before every line is priced, so a quote drafted in
  // the chat would otherwise carry a price strip beside its line editor.
  if (held.some(b => b.kind === 'line-editor')) {
    for (let i = held.length - 1; i >= 0; i--) if (held[i].kind === 'price-range') held.splice(i, 1);
  }
}

function pounds(n: number): string {
  return `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * One line per block, for the oversight transcript: enough for the grader to
 * tell "the owner had an editor" from "they did not", and nothing a customer
 * could be identified by.
 */
export function summariseUiBlock(block: UiBlock): string {
  switch (block.kind) {
    case 'line-editor':
      return `[line-editor: ${block.reference}, ${block.lines.length} line${block.lines.length === 1 ? '' : 's'}, ${pounds(block.total)}, ${block.status === 'draft' ? 'adjustable' : 'read-only'}]`;
    case 'owed-table':
      return `[owed-table: ${block.rows.length + block.more} overdue invoice${block.rows.length + block.more === 1 ? '' : 's'}, ${pounds(block.totalOwed)}, Chase on each row]`;
    case 'week-timeline': {
      const jobs = block.days.reduce((n, d) => n + d.stops.length, 0);
      return `[week-timeline: ${block.days.length} day${block.days.length === 1 ? '' : 's'}, ${jobs} job${jobs === 1 ? '' : 's'}]`;
    }
    case 'job-list':
      return `[job-list: ${block.count} job${block.count === 1 ? '' : 's'} as tappable rows]`;
    case 'approval-stack': return summariseApprovalStackBlock(block);
    case 'document-card': return summariseDocumentCardBlock(block);
    case 'revenue-chart': return summariseRevenueChartBlock(block);
    case 'client-choices': return summariseClientChoicesBlock(block);
    case 'job-brief': return summariseJobBriefBlock(block);
    case 'quote-history': return summariseQuoteHistoryBlock(block);
    case 'price-range': return summarisePriceRangeBlock(block);
    case 'certs-due': return summariseCertsDueBlock(block);
    case 'photo-picker': return summarisePhotoPickerBlock(block);
    case 'availability-strip': return summariseAvailabilityStripBlock(block);
    case 'slot-picker': return summariseSlotPickerBlock(block);
    case 'team-map': return summariseTeamMapBlock(block);
    case 'call-cards': return summariseCallCardsBlock(block);
    case 'parts-checklist': return summarisePartsChecklistBlock(block);
    case 'receipt-lines': return summariseReceiptLinesBlock(block);
    case 'morning-briefing': return summariseMorningBriefingBlock(block);
  }
}

/** Accept only what this build can draw. A stored row from a newer build, or a
 *  malformed one, is dropped rather than rendered half-understood. */
export function readUiBlocks(raw: unknown): UiBlock[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((b): b is UiBlock =>
    !!b && typeof b === 'object' && UI_BLOCK_KINDS.includes((b as { kind?: unknown }).kind as UiBlockKind));
}
